import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Sparkles } from "lucide-react";
import { T, MANROPE, FOCUS } from "../clientsTokens";

const CS = `${process.env.REACT_APP_BACKEND_URL}/api/customer-success`;
const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted };
const INPUT = "w-full h-9 rounded-[7px] px-2.5 text-[13px] mt-1 bg-white";
const EDGE = { border: `1px solid ${T.control}`, color: T.ink };

/* Paste the raw Teams notes, we structure them. Mirrors the prospect record's
   "Add meeting notes" so a meeting is logged the same way on both sides. */
export const MeetingNotesDialog = ({ client, today, onClose, onSaved }) => {
  const [f, setF] = useState({ meeting_date: today, sentiment: "none", health_score: "", raw_notes: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.raw_notes.trim()) { toast.error("Paste the meeting notes first"); return; }
    setBusy(true);
    try {
      const body = { raw_notes: f.raw_notes, meeting_date: f.meeting_date || null };
      if (f.sentiment !== "none") {
        body.sentiment = f.sentiment;
        if (f.health_score) body.health_score = parseInt(f.health_score, 10);
      }
      const res = await axios.post(`${CS}/clients/${encodeURIComponent(client.client_id)}/meetings`, body);
      toast.success(res.data?.health_check ? "Meeting saved · health check logged" : "Meeting notes saved");
      await onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the notes"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="meeting-notes-dialog">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.28)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[520px] flex flex-col"
        style={{ background: T.card, borderLeft: `1px solid ${T.border}` }}>
        <div className="px-5 py-4 flex items-start justify-between gap-3" style={{ borderBottom: `1px solid ${T.dividerInner}` }}>
          <div>
            <div style={{ ...MANROPE, fontSize: 17, fontWeight: 800, color: T.ink }}>Add meeting notes</div>
            <div className="text-[12.5px]" style={{ color: T.ink2 }}>
              Paste the raw Teams notes. We summarise them into purpose, takeaways and action items.
            </div>
          </div>
          <button onClick={onClose} data-testid="meeting-notes-close"
            className="w-8 h-8 rounded-[7px] flex items-center justify-center hover:bg-[#F6F4EF]"
            style={{ color: T.ink2 }}><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div style={LABEL}>Meeting date</div>
              <input type="date" value={f.meeting_date || ""} data-testid="meeting-notes-date"
                onChange={(e) => setF({ ...f, meeting_date: e.target.value })} className={`${INPUT} ${FOCUS}`} style={EDGE} />
            </div>
            <div>
              <div style={LABEL}>Sentiment</div>
              <select value={f.sentiment} onChange={(e) => setF({ ...f, sentiment: e.target.value })}
                data-testid="meeting-notes-sentiment" className={`${INPUT} ${FOCUS}`} style={EDGE}>
                <option value="none">No health check</option>
                <option value="positive">Positive</option>
                <option value="neutral">Neutral</option>
                <option value="negative">Negative</option>
              </select>
            </div>
            <div>
              <div style={LABEL}>Score out of 10</div>
              <input type="number" min="1" max="10" value={f.health_score}
                disabled={f.sentiment === "none"} placeholder={f.sentiment === "none" ? "—" : "auto"}
                onChange={(e) => setF({ ...f, health_score: e.target.value })} data-testid="meeting-notes-score"
                className={`${INPUT} ${FOCUS} disabled:opacity-40`} style={EDGE} />
            </div>
          </div>
          {f.sentiment !== "none" && (
            <div className="text-[11.5px]" style={{ color: T.ink2 }} data-testid="meeting-notes-hc-hint">
              A health check will be logged from this meeting and feed the client's health signal
              {f.health_score ? "" : " (score set from the sentiment if you leave it blank)"}.
            </div>
          )}
          <div>
            <div style={LABEL}>Meeting notes</div>
            <textarea rows={12} value={f.raw_notes} data-testid="meeting-notes-body"
              onChange={(e) => setF({ ...f, raw_notes: e.target.value })}
              placeholder="Paste the full AI meeting notes here — purpose, takeaways, summary, action items…"
              className={`w-full rounded-[7px] px-2.5 py-2 text-[12.5px] mt-1 ${FOCUS}`} style={EDGE} />
          </div>
        </div>

        <div className="px-5 py-3.5 flex items-center gap-2" style={{ background: T.panel, borderTop: `1px solid ${T.dividerInner}` }}>
          <button onClick={onClose} className="h-9 px-3 text-[13px]" style={{ color: T.ink2 }}>Cancel</button>
          <button onClick={save} disabled={busy || !f.raw_notes.trim()} data-testid="meeting-notes-save"
            className="ml-auto h-9 px-4 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] font-bold uppercase disabled:opacity-60"
            style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: "#000", letterSpacing: "0.04em" }}>
            <Sparkles className="w-3.5 h-3.5" /> {busy ? "Summarising…" : "Summarise & save"}
          </button>
        </div>
      </div>
    </div>
  );
};
